import { Stack, Text } from '@mantine/core';
import { DataTable, type DataTableColumn, type DataTableSortStatus } from 'mantine-datatable';
import { DisabledBadge } from '../../../components';
import { PAGE_SIZE } from '../../../magic-values';
import type { AccountDoc } from '../../../types/account';

interface AccountsTableProps {
  accounts: AccountDoc[];
  fetching?: boolean;
  onRowClick?: (account: AccountDoc) => void;
  page: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  sortStatus: DataTableSortStatus;
  onSortStatusChange: (status: DataTableSortStatus) => void;
  height?: string | number;
}

function formatDate(value: string | undefined): string {
  return value ? new Date(value).toLocaleString() : '—';
}

const columns: DataTableColumn<AccountDoc>[] = [
  {
    accessor: 'name',
    title: 'Name',
    sortable: true,
    render: (account) => (
      <Stack spacing={0}>
        <Text size="sm">{account.name ?? '—'}</Text>
        <Text size="xs" color="dimmed">
          {account._id}
        </Text>
      </Stack>
    ),
  },
  {
    accessor: 'domain',
    title: 'Domain',
    sortable: true,
    render: (account) => account.domain ?? '—',
  },
  {
    accessor: 'currency',
    title: 'Currency',
    sortable: true,
    render: (account) => account.currency ?? '—',
  },
  {
    accessor: 'timezone',
    title: 'Timezone',
    sortable: true,
    render: (account) => account.timezone ?? '—',
  },
  {
    accessor: 'disabled',
    title: 'Disabled',
    sortable: true,
    render: (account) => <DisabledBadge disabled={account.disabled} />,
  },
  {
    accessor: 'createdAt',
    title: 'Created',
    sortable: true,
    render: (account) => formatDate(account.createdAt),
  },
];

export function AccountsTable({
  accounts,
  fetching,
  onRowClick,
  page,
  totalRecords,
  onPageChange,
  sortStatus,
  onSortStatusChange,
  height = 'calc(100vh - 300px)',
}: AccountsTableProps) {
  return (
    <DataTable
      striped
      highlightOnHover
      height={height}
      sx={{ minWidth: 960 }}
      records={accounts}
      idAccessor="_id"
      fetching={fetching}
      noRecordsText="No accounts."
      columns={columns}
      onRowClick={onRowClick ? (account) => onRowClick(account) : undefined}
      totalRecords={totalRecords}
      recordsPerPage={PAGE_SIZE}
      page={page}
      onPageChange={onPageChange}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
    />
  );
}
